/*
 * *
 *   ~  * Copyright (c) by INFODATA-GROUP. All rights reserved.
 *   ~  * No part of this publication may be reproduced, distributed,
 *   ~  * or transmitted in any form or by any means, including photocopying, recording,
 *   ~  * or other electronic or mechanical methods, without the prior written permission of the publisher,
 *   ~  * except in the case of brief quotations embodied in critical
 *   ~  * reviews and certain other noncommercial uses permitted by copyright law.
 *   ~  * AUTHOR : bs
 *   ~  * DATE : 15/05/2025 17:46
 *
 */

import {
  Component,
  computed,
  inject,
  input,
  Input,
  OnChanges,
  signal,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';

import { StandardPageManager } from '../../manager/standard-page.manager';
import {
  ActionEvent,
  DataModel,
  RestError,
  RestService,
  StandardEntity,
} from '@infodata-group/inf-commons';
import type { Factory } from '@infodata-group/inf-commons';
import type { ICrud } from './interface/crud.interface';
import { StandardPageTabContainerComponent } from './standard-page-tab-container/standard-page-tab-container.component';
import {
  ActionsBarComponent,
  ActionsBarConfig,
} from '@infodata-group/inf-commons-desktop';
import { StandardPageErrorComponent } from './standard-page-error/standard-page-error.component';

@Component({
  selector: 'infl-standard-page',
  standalone: true,
  imports: [
    StandardPageTabContainerComponent,
    StandardPageErrorComponent,
    ActionsBarComponent,
  ],
  templateUrl: './standard-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './standard-page.component.scss',
})
export class StandardPageComponent<T extends StandardEntity>
  implements OnChanges
{
  @Input() manager: StandardPageManager<T>;
  protected readonly ActionsBarConfig = ActionsBarConfig;
  private readonly restService = inject(RestService);

  @Input({ required: true }) CRUD: ICrud;
  @Input({ required: true }) factory: Factory<T>;
  @Input({ required: true }) displayDetails: boolean;

  public title = input<string[]>([]);
  public icon = input<string>('cog');

  protected displayedTitle = computed(() => {
    if (this.title().length > 0) {
      return this.title();
    }
    if (this.model()) {
      return this.model().title;
    }
    return '';
  });

  public isLoading = signal(false);

  public isError = computed(() => {
    return this.errorText() !== '' && this.errorTitle() !== '';
  });
  public errorTitle = signal('');
  public errorText = signal('');

  protected model = signal<DataModel>(null);

  ngOnChanges(changes: SimpleChanges): void {
    if (Object.hasOwn(changes, 'factory')) {
      this.reload();
    }
  }

  private reload(): void {
    this.errorTitle.set('');
    this.errorText.set('');
    this.isLoading.set(true);
    this.restService
      .options(this.factory.getApi(), { isSilent: true })
      .subscribe({
        next: (dataModel) => {
          this.model.set(dataModel);
          this.isLoading.set(false);
        },
        error: (error: RestError) => {
          this.errorTitle.set(error.title);
          this.errorText.set(`${error.status} - ${error.statusText}`);
          this.isLoading.set(false);
        },
      });
  }

  protected onErrorClick($event: ActionEvent) {
    switch ($event.action.name) {
      case 'retry':
        this.reload();
        break;
    }
  }
}
